import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK } from '../api'
import { LastUpdated } from '../components/LastUpdated'
import { RefreshButton } from '../components/RefreshButton'
import { WalletBreakdown } from '../components/WalletBreakdown'
import { LoadingSkeleton } from '../components/LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'

export function Wallet() {
  // `?page=N` en la URL — así el back button del browser te lleva a la
  // página anterior del journal y se puede compartir/linkear.
  const [searchParams, setSearchParams] = useSearchParams()
  const pageParam = parseInt(searchParams.get('page') ?? '1', 10)
  const page = Number.isFinite(pageParam) && pageParam >= 1 ? pageParam : 1
  const setPage = (next: number) => {
    const p = Math.max(1, next)
    setSearchParams(
      (prev) => {
        if (p === 1) prev.delete('page')
        else prev.set('page', String(p))
        return prev
      },
      { replace: true }
    )
  }
  const { key: forceRefetchKey, force, bump } = useForceRefetch()
  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['wallet-journal', page, forceRefetchKey],
    queryFn: () => api.walletJournal(page, force),
  })
  const { data: balance, dataUpdatedAt: balanceUpdatedAt } = useQuery({
    queryKey: ['wallet-balance', forceRefetchKey],
    queryFn: () => api.walletBalance(force),
  })

  const hasEntries = (data?.entries?.length ?? 0) > 0
  // ESI devuelve hasta 1000 entradas por página. Si llenó la página,
  // probablemente haya más → habilitamos "Siguiente".
  const hasMorePages = hasEntries && (data?.entries?.length ?? 0) >= 1000

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold">Wallet</h1>
          <p className="text-eve-muted text-sm">Tu cuenta personal.</p>
        </div>
        {balance && (
          <div className="text-right">
            <div className="stat-label">Balance</div>
            <div className="stat">{formatISK(balance.balance)}</div>
            <div className="mt-0.5 flex items-center justify-end gap-2">
              <LastUpdated timestamp={balanceUpdatedAt} label="" />
              <RefreshButton onClick={bump} isFetching={isFetching} />
            </div>
          </div>
        )}
      </div>

      <div className="card">
        {isLoading ? (
          <LoadingSkeleton variant="rows" rows={6} />
        ) : !data?.entries?.length ? (
          <div className="text-eve-muted py-8 text-center">Sin entradas.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-eve-muted border-b border-eve-border">
                    <th className="py-2 font-medium">Fecha</th>
                    <th className="py-2 font-medium">Tipo</th>
                    <th className="py-2 font-medium text-right">Monto</th>
                    <th className="py-2 font-medium text-right">Balance</th>
                    <th className="py-2 font-medium">Descripción</th>
                  </tr>
                </thead>
                <tbody>
                  {data.entries.map((e) => (
                    <tr key={e.id} className="border-b border-eve-border/50">
                      <td className="py-2 text-eve-muted whitespace-nowrap">
                        {new Date(e.date).toLocaleString()}
                      </td>
                      <td className="py-2">
                        <code className="text-xs bg-eve-bg px-1.5 py-0.5 rounded">
                          {e.ref_type}
                        </code>
                      </td>
                      <td
                        className={`py-2 text-right font-mono ${
                          e.amount >= 0 ? 'text-eve-success' : 'text-eve-danger'
                        }`}
                      >
                        {e.amount >= 0 ? '+' : ''}
                        {formatISK(e.amount)}
                      </td>
                      <td className="py-2 text-right font-mono text-eve-muted">
                        {formatISK(e.balance)}
                      </td>
                      <td className="py-2 text-eve-muted text-xs">
                        {e.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-4">
              <button
                className="btn-secondary"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                ← Anterior
              </button>
              <span className="text-eve-muted text-sm">Página {page}</span>
              <button
                className="btn-secondary"
                disabled={!hasMorePages}
                onClick={() => setPage(page + 1)}
              >
                Siguiente →
              </button>
            </div>
          </>
        )}
      </div>

      <WalletBreakdown />
    </div>
  )
}
